import router from '@/router';
import _ from 'lodash';
import {
  handleGetCapsule,
  handleDestroyCapsuleRequest,
  handleDuplicateCapsule,
  handlePublishCapsuleRequest,
  handleUpdateCapsuleDetails,
  handleUpdateLayerProductSequencesRequest,
  capsulePublishableRequest
} from '@/store/api/capsule';

import CapsuleService from '@/services/api/CapsuleService';
import CapsuleLayerProductService from '@/services/api/CapsuleLayerProductService'
import {
  discoverActions,
  discoverGetters,
  discoverMutations,
  discoverState
} from './discover';

const state = {
  ...discoverState,
  addLayerProductErrors: [],
  allCapsules: [],
  capsuleLayerProductIdsPending: [],
  capsulesMetaData: {},
  currentCapsule: null,
  latestCapsules: [],
  error: '',
  updateCapsuleError: null,
};

const getters = {
  ...discoverGetters,
  addLayerProductErrors: state => state.addLayerProductErrors,
  allCapsules: state => state.allCapsules,
  capsuleLayerProductIdsPending: state => state.capsuleLayerProductIdsPending,
  capsulesMetaData: state => state.capsulesMetaData,
  currentCapsule: state => state.currentCapsule,
  currentCapsuleLayers: state => state.currentCapsule && state.currentCapsule.layers,
  latestCapsules: state => state.latestCapsules,
  updateCapsuleError: state => state.updateCapsuleError
};

const actions = {
  ...discoverActions,

  resetCurrentCapsule({ commit, dispatch }) {
    commit('SAVE_CURRENT_CAPSULE', null);
    commit('SAVE_UPDATE_CAPSULE_ERROR', null);
    dispatch('resetAllVotes')
    dispatch('resetWearLikeThisImages')
    dispatch('resetCapsuleLayerProductIdsPending')
  },

  resetAllCapsules({ commit, dispatch }) {
    commit('SAVE_CAPSULES', [])
    commit('SAVE_LATEST_CAPSULES', [])
    dispatch('resetAllVotes')
  },

  async fetchCurrentCapsule({ commit }, payload) {
    const { data, message } = await handleGetCapsule(payload);

    if (data) {
      commit('SAVE_CURRENT_CAPSULE', data.capsule);
    } else {
      commit('SAVE_ERROR_RESPONSE', message);
    }
  },

  fetchCapsules({ commit }, payload) {
    const params = {
      filter: payload.filter,
      stylistId: payload.stylistId,
      status: payload.status,
      page: payload.page,
      per_page: payload.per_page
    }

    return CapsuleService.getAll(params)
      .then((response) => {
        const capsules = response.data.capsules

        params.page && params.page > 1
          ? commit('APPEND_CAPSULES', capsules)
          : commit('SAVE_CAPSULES', capsules);

        commit('SAVE_LATEST_CAPSULES', capsules);
        commit('SAVE_CAPSULES_META_DATA', response.data.meta);
      })
      .catch(error => {
        commit('SAVE_ERROR_RESPONSE', error.message);
      })
  },

  createCapsule({ commit, dispatch }, { title, occasion, notes, link }) {
    return CapsuleService.create(title, occasion, notes, link)
      .then((response) => {
        commit('SAVE_CURRENT_CAPSULE', response.data.capsule);
        dispatch('changeUserCapsuleCount', 'increment')

        router.push({
          name: 'CapsuleBuilder',
          params: {
            username: this.state.user.currentUser.username,
            capsuleId: response.data.capsule.id,
            capsule: response.data.capsule
          }
        });
      })
      .catch(error => {
        commit('SAVE_ERROR_RESPONSE', error.message);
      })
  },

  async destroyCapsule({ commit, dispatch }, capsuleId) {
    const { data, message } = await handleDestroyCapsuleRequest(capsuleId);

    if (data) {
      commit('REMOVE_FROM_ALL_CAPSULES', capsuleId)
      dispatch('changeUserCapsuleCount', 'decrement')
    } else {
      commit('SAVE_ERROR_RESPONSE', message);
    }
  },

  async duplicateCapsule({ commit, dispatch }, payload) {
    const { data, message } = await handleDuplicateCapsule(payload);

    const response = data['capsule']

    if (response) {
      commit('APPEND_CAPSULES', response);
      dispatch('changeUserCapsuleCount', 'increment')
    } else {
      commit('SAVE_ERROR_RESPONSE', message);
    }
  },

  // Interface(:layerId, :products)
  async updateLayerProductSequences({ commit }, payload) {

    commit('UPDATE_LAYER_PRODUCT_SEQUENCES', payload)

    const { data, message } = await handleUpdateLayerProductSequencesRequest(payload);

    if (!data) {
      const errorPayload = {
        'layerId': payload['layerId'],
        'message': 'Woopsy!'
      }
      commit('SAVE_ADD_LAYER_PRODUCT_ERRORS_RESPONSE', errorPayload);
    }
  },

  async publishCapsule({ commit }, payload) {
    const { data, message } = await handlePublishCapsuleRequest(payload);

    if (data) {
      let capsuleId = data['id'];

      router.push({
        name: 'ShowCapsule', params: { capsuleId }
      });

    } else {
      commit('SAVE_ERROR_RESPONSE', message);
    }
  },

  updateCapsule({ commit }, { capsuleId, title, occasion, notes, link }) {
    commit('SAVE_UPDATE_CAPSULE_ERROR', null);

    return CapsuleService.update(capsuleId, title, occasion, notes, link)
      .then((response) => {
        commit('SAVE_CURRENT_CAPSULE', response.data.capsule);
      })
      .catch(error => {
        commit('SAVE_UPDATE_CAPSULE_ERROR', error.message);
      })
  },

  async updateCapsuleDetails({ commit }, payload) {
    commit('SAVE_UPDATE_CAPSULE_ERROR', null);

    const { data, message } = await handleUpdateCapsuleDetails(payload);

    data
      ? commit('SAVE_CURRENT_CAPSULE', data['capsule'])
      : commit('SAVE_UPDATE_CAPSULE_ERROR', message);
  },

  adjustCapsuleCounter({ commit }, payload) {
    if(!!!this._vm.$constants.CAPSULE_COUNTERS.includes(payload['counterName'])) return

    commit('CHANGE_COUNTER_IN_DISCOVER_CAPSULES', payload)
    commit('CHANGE_COUNTER_IN_CURRENT_CAPSULE', payload)
  },

  // #########################################################################
  //
  // CAPSULE BUILDER
  //
  // #########################################################################

  async checkCurrentCapsuleIsPublishable({ commit }, capsuleId) {
    const { data, message } = await capsulePublishableRequest(capsuleId);

    if (typeof data == "boolean") {
      commit('UPDATE_CURRENT_CAPSULE_PUBLISHABLE_STATE', data)
      return true;
    } else {
      // commit('SAVE_WARDROBE_ERROR_RESPONSE', message);
      return false;
    }
  },

  checkCurrentCapsulePendingProducts({ dispatch, state }) {
    const list = state.currentCapsule.layers.flatMap(layer => {
      return layer.layer_products.filter(lp => lp.product_id == null).map(x => x.id)
    })
    dispatch('resetCapsuleLayerProductIdsPending', list)
  },

  addCapsuleLayerProduct({ commit, dispatch }, { capsuleId, layerId, productId, productUrl }) {
    return CapsuleLayerProductService.create(capsuleId, layerId, productId, productUrl)
      .then((response) => {
        const layerProduct = response.data.layer_product

        commit('ADD_CAPSULE_LAYER_PRODUCT', layerProduct)
        dispatch('checkCurrentCapsuleIsPublishable', capsuleId)

        if (layerProduct.product_id === null) {
          commit('ADD_CAPSULE_LAYER_PRODUCT_ID_PENDING', layerProduct.id)
        }
      })
      .catch(error => {
        const errorPayload = {
          'layerId': payload['layerId'],
          'message': 'Woopsy!'
        }
        commit('SAVE_ADD_LAYER_PRODUCT_ERRORS_RESPONSE', errorPayload);
      })
  },

  removeCapsuleLayerProduct({ commit, dispatch }, { capsuleId, layerProduct }) {
    return CapsuleLayerProductService.destroy(capsuleId, layerProduct.id)
      .then(() => {
        commit('REMOVE_CAPSULE_LAYER_PRODUCT', layerProduct)
        commit('SYNC_CAPSULE_LAYER_PRODUCT_SEQUENCE', layerProduct.layer_id)
        dispatch('checkCurrentCapsuleIsPublishable', capsuleId)
      })
      .catch(error => { })
  },

  updateCapsuleLayerProduct({ commit }, { capsuleId, layerProductId, externalUrl }) {
    return CapsuleLayerProductService.update(capsuleId, layerProductId, externalUrl)
      .then((response) => {
        commit('UPDATE_CAPSULE_LAYER_PRODUCT', response.data.layer_product)
      })
      .catch(error => { })
  },

  resetCapsuleLayerProductIdsPending({ commit }, idList = []) {
    commit('RESET_CAPSULE_LAYER_PRODUCT_IDS_PENDING', idList);
  },

  pollPendingCapsuleProducts({ commit, state }, capsuleId) {
    if (state.capsuleLayerProductIdsPending.length <= 0) return false

    const params = {
      layerProductIds: state.capsuleLayerProductIdsPending,
      assignPendingProducts: true
    }

    return CapsuleLayerProductService.getAll(capsuleId, params)
      .then((response) => {
        response.data.layer_products.forEach(layerProduct => {
          if (layerProduct.product_id) {
            commit('UPDATE_CAPSULE_LAYER_PRODUCT', layerProduct)
            commit('REMOVE_CAPSULE_LAYER_PRODUCT_ID_PENDING', layerProduct)
          }
        })
      })
      .catch(error => { })
  }
};

const mutations = {
  ...discoverMutations,

  SAVE_CURRENT_CAPSULE(state, response) {
    state.currentCapsule = response;
  },

  SAVE_CAPSULES(state, response) {
    state.allCapsules = response;
  },

  APPEND_CAPSULES(state, response) {
    state.allCapsules = state.allCapsules.concat(response);
  },

  REMOVE_FROM_ALL_CAPSULES(state, capsuleId) {
    state.allCapsules.forEach((capsule, index) => {
      if (capsule.id === capsuleId) {
        state.allCapsules.splice(index, 1)
      }
    });
  },

  SAVE_LATEST_CAPSULES(state, response) {
    state.latestCapsules = response;
  },

  SAVE_CAPSULES_META_DATA(state, response) {
    state.capsulesMetaData = response;
  },

  SAVE_ERROR_RESPONSE(state, error) {
    state.error = error;
  },

  SAVE_ADD_LAYER_PRODUCT_ERRORS_RESPONSE(state, error) {
    state.addLayerProductErrors.push(error);
  },

  UPDATE_LAYER_PRODUCT_SEQUENCES(state, { layerId, layerProducts }) {
    state.currentCapsule.layers.forEach((layer, _index) => {
      if (layer.id === layerId) layer.layer_products = layerProducts;
    });
  },

  // #########################################################################
  //
  // COUNTERS
  //
  // #########################################################################

  CHANGE_COUNTER_IN_DISCOVER_CAPSULES(state, { capsuleId, counterName, direction}) {
    const index = state.discoverCapsules.all.findIndex(c => c.id === capsuleId)

    if (index !== -1) {

      if (direction === 'down') {
        state.discoverCapsules.all[index][counterName]--

      } else if (direction === 'up') {
        state.discoverCapsules.all[index][counterName]++
      }
    }
  },

  CHANGE_COUNTER_IN_CURRENT_CAPSULE(state, { capsuleId, counterName, direction}) {
    if (state.currentCapsule && state.currentCapsule.id === capsuleId) {

      if (direction === 'down') {
        state.currentCapsule[counterName]--

      } else if (direction === 'up') {
        state.currentCapsule[counterName]++
      }
    }
  },

  // #########################################################################
  //
  // CAPSULE BUILDER
  //
  // #########################################################################

  UPDATE_CURRENT_CAPSULE_PUBLISHABLE_STATE(state, isPublishable) {
    state.currentCapsule.publishable = isPublishable;
  },

  ADD_CAPSULE_LAYER_PRODUCT(state, layerProduct) {
    state.currentCapsule.layers.forEach(layer => {
      if (layer.id === layerProduct.layer_id) {
        layer.layer_products.push(layerProduct);
        layer.layer_products_count++
        state.currentCapsule.products_count++
      }
    })
  },

  REMOVE_CAPSULE_LAYER_PRODUCT(state, layerProduct) {
    const layerIndex = state.currentCapsule.layers.findIndex(layer => layer.id == layerProduct.layer_id);

    state.currentCapsule.layers[layerIndex].layer_products.forEach((lp, index) => {
      if (lp.id === layerProduct.id) {
        let layer = state.currentCapsule.layers[layerIndex]

        layer.layer_products.splice(index, 1)
        layer.layer_products_count--
        state.currentCapsule.products_count--
      }
    });
  },

  SAVE_UPDATE_CAPSULE_ERROR(state, message) {
    state.updateCapsuleError = message;
  },

  UPDATE_CAPSULE_LAYER_PRODUCT(state, layerProduct) {
    state.currentCapsule.layers.forEach(layer => {
      if (layer.id === layerProduct.layer_id) {
        const index = layer.layer_products.findIndex(lp => lp.id === layerProduct.id)

        if (index > -1) {
          layer.layer_products.splice(index, 1, layerProduct);
        }
      }
    })
  },

  SYNC_CAPSULE_LAYER_PRODUCT_SEQUENCE(state, layerId) {
    state.currentCapsule.layers.forEach(layer => {
      if (layer.id === layerId) {
        layer.layer_products.forEach((layer_product, index) => {
          layer_product.sequence = index
        });
      }
    });
  },

  ADD_CAPSULE_LAYER_PRODUCT_ID_PENDING(state, id) {
    state.capsuleLayerProductIdsPending.push(id)
  },

  REMOVE_CAPSULE_LAYER_PRODUCT_ID_PENDING(state, layerProduct) {
    const index = state.capsuleLayerProductIdsPending.findIndex((id) => id === layerProduct.id)

    if (index !== -1) {
      state.capsuleLayerProductIdsPending.splice(index, 1);
    }
  },

  RESET_CAPSULE_LAYER_PRODUCT_IDS_PENDING(state, list) {
    state.capsuleLayerProductIdsPending = list
  },
};

export default {
  state,
  getters,
  actions,
  mutations
};



// WEBPACK FOOTER //
// ./src/store/modules/capsule/index.js